// Registro de asistencia para empleados

import React, { useEffect, useState, useMemo } from 'react';
import { useUser } from '../UserContext';
import {
  Box
} from "@mui/material";
import ShiftsTable from './ShiftsTable';
import RegisterAttendanceCard from './RegisterAttendanceCard';
import { apiFetchJson } from '../fetchWrapper';
import MapWidget from './MapWidget';

export default function RegisterAttendanceForEmployee() {
  const { user, setUser, hasRole } = useUser();
  const [sites, setSites] = useState([]);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [scheduledShifts, setScheduledShifts] = useState([]);
  const [attendances, setAttendances] = useState([]);

  const employeeId = user?.id;

  // Cargar todos los datos iniciales
  useEffect(() => {
    apiFetchJson("/api/sites").then(setSites);
    apiFetchJson("/api/shift-templates").then(setShiftTemplates);
  }, []);

  // Cargar turnos programados para employeeId
  useEffect(() => {
    if (!employeeId) {
      setScheduledShifts([]);
      return;
    }

    apiFetchJson('/api/scheduled-shifts?employeeId=' + employeeId).then(data => setScheduledShifts(Array.isArray(data) ? data : []));
    apiFetchJson('/api/attendances?employeeId=' + employeeId).then(data => setAttendances(Array.isArray(data) ? data : []));
  }, [employeeId]);


  const sortShifts = (a, b, { asc = true } = {}) => {
    const dir = asc ? 1 : -1;

    const dateDiff = new Date(a.shiftDate) - new Date(b.shiftDate);
    if (dateDiff !== 0) return dateDiff * dir;

    const templatediff = a.shiftTemplateId - b.shiftTemplateId;
    return templatediff * dir;
  };

  const sortedShifts = useMemo(() => {
    return scheduledShifts
      .sort((a, b) => sortShifts(a, b, { asc: true }));
  }, [scheduledShifts]);

  const templateMap = Object.fromEntries(
    shiftTemplates.map(t => [t.id, t])
  );

  const getSite = (siteId) => {
    return sites.find(s => s.id === siteId) || siteId;
  };

  const getTemplate = (templateId) => {
    return shiftTemplates.find(t => t.id === templateId) || templateId;
  };

  const setNextShiftAttendance = (updatedAttendance) => {
    if (!updatedAttendance || !updatedAttendance.id) {
      return;
    }
    setAttendances(prev => {
      const list = Array.isArray(prev) ? prev : [];
      const exists = list.some(a => a.id === updatedAttendance.id);

      if (exists) {
        return list.map(a =>
          a.id === updatedAttendance.id ? updatedAttendance : a
        );
      } else {
        return [...list, updatedAttendance];
      }
    });
  };

  const now = new Date();

  const getShiftStartEnd = (shift) => {
    const template = templateMap[shift.shiftTemplateId];
    if (!template) return null;

    const start = new Date(`${shift.shiftDate}T${template.startTime}`);
    let end = new Date(`${shift.shiftDate}T${template.endTime}`);

    // si pasa de medianoche, sumar un día a la fecha de fin
    if (end <= start) {
      end.setDate(end.getDate() + 1);
    }

    return { start, end };
  };

  const shiftsWithTime = useMemo(() => {
    return sortedShifts
      .map(s => {
        const times = getShiftStartEnd(s);
        return times ? { ...s, ...times } : null;
      })
      .filter(Boolean); // remove nulls
  }, [sortedShifts]);

  const currentShift = useMemo(() => {
    return shiftsWithTime.find(
      s => s.start <= now && s.end >= now
    ) || null;
  }, [sortedShifts]);

  const nextShift = shiftsWithTime
    .filter(s => s.start > now)
    .sort((a, b) => a.start - b.start)[0] || null;

  const lastShift = shiftsWithTime
    .filter(s => s.end < now)
    .sort((a, b) => b.start - a.start)[0] || null;

  const activeShift = currentShift || nextShift || lastShift;
  const safeAttendances = Array.isArray(attendances) ? attendances : [];
  const activeShiftAttendance = activeShift ? safeAttendances.find(a => a.scheduledShiftId === activeShift.id) : null;

  const upcomingShiftsSorted = [...shiftsWithTime]
    .filter(s => new Date(s.start) >= now);

  const pastShifts = [...shiftsWithTime]
    .filter(s => new Date(s.end) < now).reverse();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%'
      }}
    >
      {activeShift && (
        <RegisterAttendanceCard
          nextShift={activeShift}
          nextShiftAttendance={activeShiftAttendance}
          setNextShiftAttendance={setNextShiftAttendance}
          selectedEmployee={user}
        />
      )}

      <ShiftsTable
        title="Turnos próximos"
        shifts={upcomingShiftsSorted}
        getSite={getSite}
        getTemplate={getTemplate}
      />

      <ShiftsTable
        title="Turnos pasados"
        shifts={pastShifts}
        getSite={getSite}
        getTemplate={getTemplate}
      />

    </Box>
  )
}